'use client'

import { FC, memo, useMemo } from 'react'
import cn from 'classnames'
import type { PaymentMethod } from '@/root/shared-types/reservation/reservation-types'
import { useGetTranslations } from '@/root/shared-hooks'
import { getEnabledPaymentMethodsInDisplayOrder, sanitizeHtml } from '@/root/shared-helpers'
import { Checkbox } from '@/root/ui/tuuleliinid'
import { getStyledHTML } from '@/root/utils/getStyledHTML'
import { tuuleliinid } from '@/root/shared-consts/theme'
import { testIds, testIdHelpers } from '@/root/shared-consts'
import { CreditPaymentButton } from '@/root/components/tuuleliinid/CreditPaymentButton/CreditPaymentButton'
import { EditActions } from '../EditActions'

interface PaymentSectionProps {
  methods: PaymentMethod[]
  selectedMethod: PaymentMethod | null
  onSelectMethod: (method: PaymentMethod) => void
  isTermsAccepted: boolean
  onToggleTerms: () => void
  isBusy: boolean
  canPay: boolean
  isProcessing: boolean
  isCancelling: boolean
  toPayAmount: number
  termsText?: string
  onPay: () => void
  onCancel: () => void
  creditBalance: number
  isCreditBalanceLoading: boolean
  isCreditInsufficient: boolean
}

export const PaymentSection: FC<PaymentSectionProps> = memo(
  ({
    methods,
    selectedMethod,
    onSelectMethod,
    isTermsAccepted,
    onToggleTerms,
    isBusy,
    canPay,
    isProcessing,
    isCancelling,
    toPayAmount,
    termsText,
    onPay,
    onCancel,
    creditBalance,
    isCreditBalanceLoading,
    isCreditInsufficient,
  }) => {
    const { getTranslation } = useGetTranslations()

    const enabledMethods = useMemo(() => getEnabledPaymentMethodsInDisplayOrder(methods), [methods])

    const styledTerms = termsText ? sanitizeHtml(getStyledHTML({ theme: tuuleliinid, body: termsText }) ?? '') : ''

    return (
      <>
        {/* Payment methods + Terms */}
        {enabledMethods.length > 0 && (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <div className="text-xl font-bold">{getTranslation('paymentMethodsTitle')}</div>
              <div data-testid={testIds.edit.paymentMethods} className="flex flex-wrap items-start justify-start gap-5">
                {enabledMethods.map((method) =>
                  method.code === 'CREDIT' ? (
                    <CreditPaymentButton
                      key={method.code}
                      testId={testIdHelpers.editPaymentMethod(method.code)}
                      creditBalance={creditBalance}
                      selected={selectedMethod?.code === method.code}
                      onSelect={() => onSelectMethod(method)}
                      disabled={isBusy || isCreditInsufficient || isCreditBalanceLoading}
                      isBalanceLoading={isCreditBalanceLoading}
                    />
                  ) : (
                    <button
                      key={method.code}
                      data-testid={testIdHelpers.editPaymentMethod(method.code)}
                      type="button"
                      onClick={() => onSelectMethod(method)}
                      disabled={isBusy}
                      className={cn(
                        'flex h-30 w-45 flex-col items-center justify-center gap-2.5 rounded-2xl border py-4',
                        selectedMethod?.code === method.code
                          ? 'border-success bg-success-very-light border-2'
                          : 'border-neutral-medium-lighter hover:border-neutral-medium bg-white'
                      )}
                    >
                      <div className="flex h-10 items-center justify-center">
                        {'logoUrl' in method && (
                          <img src={method.logoUrl} alt={method.title} className="max-h-10 max-w-25 object-contain" />
                        )}
                      </div>
                      <span className="text-neutral-darker w-full px-4 text-center text-sm leading-tight">
                        {method.title}
                      </span>
                    </button>
                  )
                )}
              </div>
            </div>

            {styledTerms && (
              <div
                data-testid={testIds.edit.paymentTerms}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg border p-4',
                  isTermsAccepted ? 'border-success bg-success-very-light' : 'bg-warning-very-light border-warning-dark'
                )}
              >
                <div>
                  <Checkbox
                    testId={testIds.edit.paymentTermsCheckbox}
                    className="text-neutral-darker mb-2 gap-2 uppercase"
                    variant="success"
                    label={`${getTranslation('confirmationTermsAndConditionsTermsQuestions')}:`}
                    checked={isTermsAccepted}
                    onChange={onToggleTerms}
                  />
                  <div
                    className={cn(
                      'flex flex-col items-start gap-3 text-base font-normal',
                      isTermsAccepted ? 'text-success-dark' : 'text-warning-dark'
                    )}
                    dangerouslySetInnerHTML={{ __html: styledTerms }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payment mode actions */}
        <EditActions
          mode="payment"
          canPay={canPay}
          isProcessing={isProcessing}
          isCancelling={isCancelling}
          toPayAmount={toPayAmount}
          onCancel={onCancel}
          onPay={onPay}
        />
      </>
    )
  }
)

PaymentSection.displayName = 'PaymentSection'
